import React, { useEffect, useMemo, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import CircularLoader from './CircularLoader';
import { getAllItems, subscribeNewsletter } from '../services/api';

const ShopMain = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('Default');
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cartMessage, setCartMessage] = useState('');
  const [addedProductId, setAddedProductId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasOpenedFromShare, setHasOpenedFromShare] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart, getTotalItems } = useCart();

  const resetFilters = () => {
    setSelectedCategory('All');
    setSortBy('Default');
    // Reset to full min/max range derived from products
    setPriceRange([priceBounds.min, priceBounds.max]);
    setCurrentPage(1);
  };
  const handleAddToCart = (product, qty = 1) => {
    for (let i = 0; i < qty; i++) {
      addToCart(product);
    }
    // Local inline toast near the clicked product card
    setAddedProductId(product.id);
    setTimeout(() => setAddedProductId(null), 2000);
  };

  const handleShareProduct = (product) => {
    if (!product) return;
    const origin = window.location.origin;
    const nameSlug = slugify(product.name);
    const shareUrl = `${origin}/shop?productId=${encodeURIComponent(
      product.id
    )}&name=${encodeURIComponent(nameSlug)}`;
    const shareText = `Check out this product: ${product.name}`;

    if (navigator.share) {
      navigator
        .share({
          title: product.name,
          text: shareText,
          url: shareUrl,
        })
        .catch((err) => {
          console.error('Error sharing product:', err);
        });
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          alert('Product link copied to clipboard');
        })
        .catch((err) => {
          console.error('Failed to copy product link:', err);
        });
    } else {
      alert('Sharing is not supported in this browser');
    }
  };

  const slugify = (text) =>
    (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const openProductPage = (product) => {
    if (!product?.id) return;
    // Use parentId if this is a multi-image product, otherwise use the id
    const idToUse = product.parentId || product.id;
    const slug = slugify(product?.name || 'product');
    navigate(`/product/${encodeURIComponent(idToUse)}/${encodeURIComponent(slug)}`);
  };

  const normalizePhotoValue = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (trimmed.startsWith('data:image')) return trimmed;
    if (trimmed.startsWith('image/upload/https://') || trimmed.startsWith('image/upload/http://')) {
      return trimmed.replace(/^image\/upload\//, '');
    }
    return trimmed;
  };

  // Load items from API
  useEffect(() => {
    let cancelled = false;

    const loadItems = async () => {
      try {
        if (!cancelled) setIsLoading(true);
        const response = await getAllItems();
        const items = response?.items || response || [];

        const mapped = Array.isArray(items)
          ? items.map((item) => {
              const priceNumber = parseFloat(item.price) || 0;

              let image = '';
              const candidateSingleRaw = item.photo || item.image;
              const candidateSingle = normalizePhotoValue(candidateSingleRaw);
              const candidateArrayRaw = item.images || [];
              const candidateArray = Array.isArray(candidateArrayRaw)
                ? candidateArrayRaw
                    .map((img) => {
                      if (!img) return '';
                      if (typeof img === 'string') return normalizePhotoValue(img);
                      if (typeof img === 'object' && typeof img.image === 'string') return normalizePhotoValue(img.image);
                      return '';
                    })
                    .filter(Boolean)
                : [];

              if (candidateSingle && typeof candidateSingle === 'string' && candidateSingle.trim() !== '') {
                image = candidateSingle;
              } else if (Array.isArray(candidateArray) && candidateArray.length > 0) {
                const thumbObj = Array.isArray(candidateArrayRaw)
                  ? candidateArrayRaw.find((x) => x && typeof x === 'object' && x.is_thumbnail)
                  : null;
                if (thumbObj && typeof thumbObj.image === 'string') {
                  image = normalizePhotoValue(thumbObj.image);
                } else {
                  image = candidateArray[0] || '';
                }
              }

              return {
                id: item.id,
                name: item.name,
                price: priceNumber,
                originalPrice: null,
                rating: item.average_rating || 0,
                reviews: item.reviews_count || 0,
                image,
                images: candidateArray, // Store all images for carousel
                sale: false,
                category: item.category || '',
                description: item.description || '',
                categories: item.categories || [],
              };
            })
          : [];

        if (!cancelled) setProducts(mapped);
      } catch (err) {
        console.error('Failed to load shop items:', err);
        if (!cancelled) {
          setProducts([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadItems();

    return () => {
      cancelled = true;
    };
  }, []);

  const priceBounds = useMemo(() => {
    const prices = products.map((p) => Number(p.price) || 0).filter((n) => Number.isFinite(n));
    if (prices.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [products]);

  // Initialize price range once products are loaded so we don't hide items by default
  useEffect(() => {
    if (!products.length) return;
    setPriceRange([priceBounds.min, priceBounds.max]);
    setCurrentPage(1);
  }, [products.length, priceBounds.min, priceBounds.max]);

  const categories = useMemo(() => {
    const counts = products.reduce((acc, p) => {
      const key = String(p.category || '').trim();
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const list = Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [{ name: 'All', count: products.length }, ...list];
  }, [products]);

  // When arriving via a link with ?category=..., pre-select that category
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      const normalized = categoryParam.trim().toLowerCase();
      const match = categories.find((c) => String(c.name).toLowerCase() === normalized);
      if (match) {
        setSelectedCategory(match.name);
      }
    }
  }, [location.search]);

  // Open product modal automatically when arriving via a shared link
  useEffect(() => {
    if (hasOpenedFromShare || products.length === 0) return;

    const params = new URLSearchParams(location.search);
    const sharedId = params.get('productId');

    if (!sharedId) return;

    const product = products.find((p) => String(p.id) === String(sharedId));
    if (product) {
      openProductPage(product);
      setHasOpenedFromShare(true);
    }
  }, [location.search, products, hasOpenedFromShare]);

  const StarRating = ({ rating, reviews, showReviews = false }) => {
    return (
      <div className="flex items-center space-x-1 mb-3">
        <div className="flex space-x-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <svg
              key={star}
              className={`w-5 h-5 ${star <= rating ? 'text-orange-400' : 'text-gray-300'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
        {showReviews && (
          <span className="text-gray-500 text-sm ml-2">({reviews} customer reviews)</span>
        )}
      </div>
    );
  };

  const handleWhatsApp = (product) => {
    if (!product) return;
    const message = `Hello Brand Decor, I would like to inquire about this product: ${product.name}.`;
    const url = `https://wa.me/254714139461?text=${encodeURIComponent(message)}`;
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Apply category filter first, then price range filter
  const categoryFilteredProducts = selectedCategory === 'All'
    ? products
    : products.filter((product) => String(product.category || '').toLowerCase() === String(selectedCategory).toLowerCase());

  const keywordFilteredProducts = categoryFilteredProducts.filter((product) => {
    const q = String(searchQuery || '').trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      product.name,
      product.description,
      product.category,
      Array.isArray(product.categories) ? product.categories.join(' ') : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });

  const filteredProducts = keywordFilteredProducts.filter((product) => {
    const price = Number(product.price) || 0;
    return price >= priceRange[0] && price <= priceRange[1];
  });

  // Expand products with multiple images into separate product cards
  const expandedProducts = [];
  filteredProducts.forEach((product) => {
    if (Array.isArray(product.images) && product.images.length > 0) {
      // Create separate card for each image
      product.images.forEach((img, idx) => {
        expandedProducts.push({
          ...product,
          id: `${product.id}-${idx}`, // Unique ID for each card
          image: img,
          isMultiImage: true,
          parentId: product.id,
          imageIndex: idx,
        });
      });
    } else {
      // Single image product
      expandedProducts.push({
        ...product,
        isMultiImage: false,
      });
    }
  });

  // Apply sort dropdown
  const sortedProducts = [...expandedProducts].sort((a, b) => {
    if (sortBy === 'Price Low to High') {
      return (a.price || 0) - (b.price || 0);
    }
    if (sortBy === 'Price High to Low') {
      return (b.price || 0) - (a.price || 0);
    }
    if (sortBy === 'Highest Rated') {
      return (b.rating || 0) - (a.rating || 0);
    }
    if (sortBy === 'Newest') {
      // Fallback: assume higher id means newer
      return (b.id || 0) - (a.id || 0);
    }
    return 0; // Default: no extra sorting
  });

  const itemsPerPage = 6;
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage);

  const handleNewsletterSubscribe = async () => {
    if (!newsletterEmail.trim()) {
      setNewsletterStatus('error');
      setNewsletterMessage('Please enter your email address.');
      return;
    }

    try {
      setNewsletterStatus('loading');
      setNewsletterMessage('');
      const res = await subscribeNewsletter(newsletterEmail.trim());
      setNewsletterStatus('success');
      setNewsletterMessage(res?.message || 'Subscribed successfully!');
      setNewsletterEmail('');
    } catch (e) {
      setNewsletterStatus('error');
      setNewsletterMessage(e.message || 'Failed to subscribe. Please try again.');
    }
  };

  return (
    <div className="bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Shop</h1>
            <p className="text-sm text-gray-600 mt-1">
              Showing {sortedProducts.length} item(s)
            </p>
          </div>
          <div className="bg-gray-900 text-white px-4 py-2 rounded-lg font-medium">
            Cart ({getTotalItems()})
          </div>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-1/4">
            {/* Category Filter */}
            <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4">Category</h3>
              <div className="space-y-3">
                {categories.map((category) => (
                  <label key={category.name} className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="category"
                        checked={selectedCategory === category.name}
                        onChange={() => {
                          setSelectedCategory(category.name);
                          setCurrentPage(1);
                        }}
                        className="w-4 h-4 text-gray-900 bg-gray-100 border-gray-300 focus:ring-gray-900"
                      />
                      <span className="ml-3 text-sm text-gray-700">{category.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">({category.count})</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4">Price Range</h3>
              <div className="space-y-4">
                <input
                  type="range"
                  min={priceBounds.min}
                  max={priceBounds.max}
                  value={priceRange[1]}
                  onChange={(e) => {
                    setPriceRange([priceRange[0], parseInt(e.target.value)]);
                    setCurrentPage(1);
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-600">
                  <span>KES {priceRange[0].toLocaleString()}</span>
                  <span>KES {priceRange[1].toLocaleString()}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="w-full mt-4 bg-gray-900 hover:bg-gray-800 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            {/* Header with Sort */}
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">All Products</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search products..."
                    className="w-64 max-w-[50vw] rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                  {searchQuery.trim() && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('');
                        setCurrentPage(1);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                      title="Clear"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-gray-900 text-white px-6 py-3 rounded-lg font-medium appearance-none cursor-pointer pr-10"
                  >
                    <option value="Default">Sort By: Default</option>
                    <option value="Price Low to High">Price: Low to High</option>
                    <option value="Price High to Low">Price: High to Low</option>
                    <option value="Newest">Newest First</option>
                    <option value="Highest Rated">Highest Rated</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Grid */}
            {isLoading && products.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-10">
                <CircularLoader label="Loading products..." size="lg" />
              </div>
            ) : sortedProducts.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
                <div className="text-lg font-semibold text-gray-900">No products found</div>
                <div className="text-sm text-gray-600 mt-1">Try a different keyword or category.</div>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 inline-flex items-center rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
                >
                  Reset filters
                </button>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {paginatedProducts.map((product) => (
                <div key={product.id} className="relative bg-white rounded-lg shadow-sm overflow-hidden group hover:shadow-lg transition-shadow duration-300 cursor-pointer" onClick={() => openProductPage(product)}>
                  {/* Image Carousel */}
                  <div className="relative">
                    {Array.isArray(product.images) && product.images.length > 1 ? (
                      <>
                        <img 
                          src={product.images[product.imageIndex || 0]} 
                          alt={`${product.name} ${(product.imageIndex || 0) + 1}`}
                          className="w-full h-64 object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = product.image;
                          }}
                        />
                        {/* Image Navigation Dots */}
                        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                          {product.images.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                const newProducts = [...products];
                                const prodIdx = newProducts.findIndex(p => p.id === product.id);
                                if (prodIdx !== -1) {
                                  newProducts[prodIdx] = { ...newProducts[prodIdx], imageIndex: idx };
                                  setProducts(newProducts);
                                }
                              }}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                (product.imageIndex || 0) === idx 
                                  ? 'bg-white' 
                                  : 'bg-white/50 hover:bg-white/75'
                              }`}
                              aria-label={`View image ${idx + 1}`}
                            />
                          ))}
                        </div>
                        {/* Image Counter Badge */}
                        {product.isMultiImage && (
                          <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                            View {product.imageIndex + 1}
                          </div>
                        )}
                      </>
                    ) : (
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-64 object-cover"
                      />
                    )}
                    
                    {/* Quick View Icon */}
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" onClick={(e) => e.stopPropagation()}>
                      <button 
                        onClick={() => openProductPage(product)}
                        className="bg-white/90 p-2 rounded-full shadow-md hover:bg-white"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="p-6 text-center">
                    {/* Rating (display only on card) */}
                    <StarRating 
                      rating={product.rating} 
                      reviews={product.reviews}
                    />

                    {/* Product Name */}
                    <h3 className="font-semibold text-gray-900 mb-3 text-lg">{product.name}</h3>

                    {/* Price */}
                    <div className="flex items-center justify-center space-x-2 mb-4">
                      {product.originalPrice && (
                        <span className="text-gray-400 line-through text-sm">KES {product.originalPrice.toLocaleString()}</span>
                      )}
                      <span className="text-xl font-bold text-gray-900">KES {product.price.toLocaleString()}</span>
                    </div>

                    {/* Rating UI is available in the dedicated product details page. */}

                    <div className="space-y-3 mt-2">
                      {/* Add to Cart Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(product);
                        }}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white py-2.5 px-4 rounded-lg font-semibold transition-colors touch-manipulation"
                      >
                        Add to Cart
                      </button>

                      {/* WhatsApp Order Button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWhatsApp(product);
                        }}
                        className="w-full inline-flex items-center justify-start bg-black text-white py-2.5 px-3 rounded-full font-semibold transition-all duration-300 hover:bg-gray-900"
                      >
                        <span className="flex items-center justify-center w-9 h-9 rounded-full bg-green-500 text-white mr-2 shadow-sm">
                          <FaWhatsapp className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <div className="flex flex-col leading-tight">
                          <span className="text-xs text-green-400">Order On</span>
                          <span className="text-sm font-bold">Whatsapp</span>
                        </div>
                      </button>
                    </div>

                    {/* Inline Toast Positioned Near Button */}
                    {addedProductId === product.id && (
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-20 bg-black text-white text-xs md:text-sm px-3 py-2 rounded-md shadow-lg pointer-events-none">
                        Added to cart
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2">
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-gray-700 font-medium hover:text-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <button 
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`w-10 h-10 rounded-lg font-semibold transition-colors ${
                        currentPage === pageNumber 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-white text-gray-700 border hover:bg-gray-100'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                
                <button 
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-gray-700 font-medium hover:text-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Features Section */}
        <div className="mt-16 py-12 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* Award-Winning Design */}
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <h3 className="font-bold text-gray-800 text-lg mb-2">Award-Winning Design</h3>
              <p className="text-gray-600 text-sm">Recognized globally for innovative furniture designs</p>
            </div>

            {/* Sustainable Materials */}
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L13.09 8.26L22 9L17 14L18.18 22L12 19L5.82 22L7 14L2 9L10.91 8.26L12 2Z"/>
                  <path d="M7 13l3 3 7-7"/>
                </svg>
              </div>
              <h3 className="font-bold text-gray-800 text-lg mb-2">Sustainable Materials</h3>
              <p className="text-gray-600 text-sm">Eco-friendly production with responsibly sourced materials</p>
            </div>

            {/* Fast Delivery */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"/>
                </svg>
              </div>
              <h3 className="font-bold text-gray-800 text-lg mb-2">Fast Delivery</h3>
              <p className="text-gray-600 text-sm">Quick and reliable shipping to your doorstep</p>
            </div>

            {/* Expert Craftsmanship */}
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"/>
                </svg>
              </div>
              <h3 className="font-bold text-gray-800 text-lg mb-2">Expert Craftsmanship</h3>
              <p className="text-gray-600 text-sm">Handcrafted with attention to every detail</p>
            </div>

          </div>
        </div>

        {/* Promotional Email Signup Section */}
        <div className="mt-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="relative z-10">
            <div className="mb-6">
              <svg className="w-16 h-16 text-white mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
            </div>
            <h2 className="text-4xl font-bold text-white mb-2">
              SUBSCRIBE TO OUR
            </h2>
            <h2 className="text-4xl font-bold text-white mb-6">
              NEWSLETTER
            </h2>
            <p className="text-white text-xl mb-8">Be the first to know about new arrivals, offers, and decor inspiration.</p>
            
            <div className="flex justify-center max-w-md mx-auto">
              <div className="flex w-full">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="flex-1 px-6 py-4 rounded-l-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                />
                <button
                  type="button"
                  onClick={handleNewsletterSubscribe}
                  disabled={newsletterStatus === 'loading'}
                  className="bg-black hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-r-lg font-medium transition-colors"
                >
                  {newsletterStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
            </div>
            {newsletterMessage && (
              <p
                className={`mt-4 text-sm ${
                  newsletterStatus === 'success' ? 'text-emerald-100' : 'text-red-100'
                }`}
              >
                {newsletterMessage}
              </p>
            )}
          </div>
          
          {/* Decorative furniture image */}
          <div className="absolute left-8 bottom-0 opacity-20">
            <div className="w-32 h-32 bg-orange-300 rounded-t-full"></div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ShopMain;
